<template>
  <v-app-bar
    dense
    class="zoox-component zoox-header elevation-0"
    scroll-target="#zoox-content"
    :color="color"
    :dark="dark"
    :absolute="isMobile"
    :class="{'is-mobile': isMobile, zooxblue}"
    :hide-on-scroll="hideOnScroll"
  >
    <template v-if="$scopedSlots['extension']" v-slot:extension>
      <slot name="extension"></slot>
    </template>
    <slot name="product">
      <v-img v-if="withLogo" class="logo"></v-img>
      <span v-if="logoDivider" class="logo-divider"></span>
      <v-toolbar-title
        class="app-title"
        :style="{color: dark ? 'white' : null}"
      >
        {{ product }}</v-toolbar-title
      >
    </slot>
    <v-spacer></v-spacer>
    <slot />
    <z-descrete-select
      v-if="langs.length"
      class="toolbar-input lang"
      :value="lang"
      :items="langs"
      :hide-icon="isMobile"
      @change="onChangeLang"
    />

    <v-divider
      v-if="!isMobile && langs.length"
      vertical
      :dark="dark"
      class="zoox-header-divider"
      :style="{borderColor: dark ? 'white' : null}"
    ></v-divider>

    <slot name="user-menu">
      <z-user-menu
        :email="userEmail"
        :company="company"
        :username="username"
        :avatar-src="srcAvatar"
        @logout="onClickLogout"
      >
        <template #activator="{on}">
          <z-btn primary small color="transparent" :text="username" v-on="on">
            <template #text>
              <label v-if="!isMobile" class="username-label">
                {{ username }}
              </label>
              <v-avatar
                v-if="srcAvatar || avatarName !== ''"
                size="24"
                class="zoox-header-avatar"
                :class="{'ml-2': !isMobile}"
                color="var(--black-100)"
                v-on="on"
              >
                <img v-if="srcAvatar" :src="srcAvatar" alt="Profile Avatar" />
                <span v-else>{{ avatarName }}</span>
              </v-avatar>
            </template>
          </z-btn>
        </template>
        <slot name="username"></slot>
      </z-user-menu>
    </slot>
  </v-app-bar>
</template>

<script>
import zDescreteSelect from '../../../components/atoms/Inputs/DescreteSelect/DescreteSelect.vue'

/**
 * @displayName Header
 */
export default {
  name: 'ZHeader',
  components: {zDescreteSelect},
  props: {
    product: {
      type: String,
      required: true,
    },
    withLogo: {
      type: Boolean,
      required: false,
      default: false,
    },
    isMobile: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideOnScroll: {
      type: Boolean,
      required: false,
      default: true,
    },
    username: {
      type: String,
      required: false,
      default: '',
    },
    zooxblue: {
      type: Boolean,
      default: false,
    },
    logoDivider: {
      type: Boolean,
      default: false,
    },
    lang: {
      type: String,
      required: false,
      default: 'en',
    },
    /**
     * Linguagens disponíveis no select. Utilizar somente
     * en-pt-es. Ex: ['en'] -> Somente ingles
     */
    langOptions: {
      type: Array,
      required: false,
      default: () => ['pt', 'en'],
    },
    srcAvatar: {
      type: [Object, String],
      required: false,
    },
    /**
     * Empresa que o produto está
     */
    company: {
      type: String,
      default: '',
    },
    /**
     * Email do usuário
     */
    userEmail: {
      type: String,
      default: '',
    },
    /**
     * Flag para indicar que o texto deve ser branco
     */
    dark: {
      type: Boolean,
      default: false,
    },
    /**
     * Define a cor do header
     */
    color: {
      type: String,
      default: 'rgba(0,0,0,0)',
    },
  },
  computed: {
    langs() {
      if (this.isMobile) {
        return this.langOptions.map((lang) => ({text: lang, value: lang}))
      }
      const mapper = {
        pt: {text: this.$t('portuguese'), value: 'pt'},
        en: {text: this.$t('english'), value: 'en'},
        es: {text: this.$t('spanish'), value: 'es'},
        tr: {text: this.$t('turkish'), value: 'tr'},
        fr: {text: this.$t('french'), value: 'fr'},
      }
      return this.langOptions.map((lang) => mapper[lang])
    },
    avatarName() {
      return this.username !== '' ? this.username[0] : ''
    },
  },
  methods: {
    onChangeLang(lang) {
      /**
       * Emite o evento de alterar linguagem
       */
      this.$emit('change-lang', lang)
    },
    onClickLogout() {
      /**
       * Emite o evento para fazer logout
       */
      this.$emit('logout')
    },
  },
  i18n: {
    messages: {
      pt: {
        portuguese: 'Português',
        english: 'English',
        spanish: 'Español',
        french: 'Français',
        turkish: 'Türkçe',
        logout: 'Logout',
      },
      es: {
        portuguese: 'Português',
        english: 'English',
        spanish: 'Español',
        french: 'Français',
        turkish: 'Türkçe',
        logout: 'Logout',
      },
      en: {
        portuguese: 'Português',
        english: 'English',
        spanish: 'Español',
        french: 'Français',
        turkish: 'Türkçe',
        logout: 'Logout',
      },
      fr: {
        portuguese: 'Português',
        english: 'English',
        spanish: 'Español',
        french: 'Français',
        turkish: 'Türkçe',
        logout: 'Déconnexion',
      },
      tr: {
        portuguese: 'Português',
        english: 'English',
        spanish: 'Español',
        french: 'Français',
        turkish: 'Türkçe',
        logout: 'Çıkış',
      },
    },
  },
}
</script>

<style lang="scss">
.zoox-header {
  & > div {
    padding-left: 15px;
    padding-right: 20px;
    margin-left: -5px;
    margin-right: -5px;
  }

  & > div > * {
    margin-right: 5px;
    margin-left: 5px;
  }

  .zoox-header-avatar {
    cursor: pointer;
  }

  .zoox-header-divider {
    height: 28px;
    max-height: 28px;
    min-height: unset;
    margin-top: 10px;
  }

  .logo-divider {
    height: 20px;
    width: 2px;
    background-color: var(--primary);
    margin-right: 8px;
  }

  .app-title {
    font-weight: 800;
    color: var(--primary);
  }

  &.zooxblue {
    .logo-divider {
      background-color: var(--zooxblue);
    }
    .app-title {
      color: var(--zooxblue);
    }
  }

  &.is-mobile {
    background: rgba(244, 244, 244, 0.7) !important;
    backdrop-filter: blur(4px);

    .app-title {
      font-weight: 500;
      font-size: 1.25rem;
      color: var(--primary);
    }

    &.zooxblue {
      color: var(--zooxblue);
    }
  }

  .username-label {
    font-size: 0.875rem;
    text-transform: capitalize;
    font-weight: 400;
  }

  .logo {
    content: url('../../../assets/img/logo-menu-fixed.svg');
    height: 32px;
    max-width: 32px;
    margin-right: 10px;
  }

  .toolbar-input {
    max-width: 200px;
  }
}

.no-padding {
  padding: 0 !important;
}
</style>
